import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    const { grid } = this.refs;
    if (grid) {
      this.#syncImageSizes();
      // Cards appended by infinite scroll need the same treatment.
      this.#gridObserver = new MutationObserver(this.#syncImageSizes);
      this.#gridObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #gridObserver;

  /**
   * Card images declare 50vw for the default two-column phone grid. In the one-column view they need 100vw,
   * or phones would keep showing the smaller download.
   */
  #syncImageSizes = () => {
    const { grid } = this.refs;
    if (grid?.getAttribute('product-grid-view') !== 'mobile-single') return;

    for (const image of grid.querySelectorAll('img.product-media__image[sizes$="50vw"]')) {
      if (image instanceof HTMLImageElement) image.sizes = image.sizes.replace(/50vw$/, '100vw');
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncImageSizes();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
